import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, UserPlus, ChevronRight, Loader2,
  User, Briefcase, CreditCard, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STEPS = [
  { id: "personal",   label: "Datos personales",  icon: User },
  { id: "laboral",    label: "Datos laborales",    icon: Briefcase },
  { id: "compensacion", label: "Compensación",     icon: CreditCard },
];

const POSITIONS = [
  { code: "GTE00", label: "Gerente" },
  { code: "SG01",  label: "Sub Gerente" },
  { code: "ENT01", label: "Entrenador" },
  { code: "CJA01", label: "Cajero / Caja" },
  { code: "COC01", label: "Cocina" },
  { code: "EG01",  label: "Empleado General" },
  { code: "ADM01", label: "Administrativo" },
];

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />{error}
        </p>
      )}
    </div>
  );
}

export default function OpsRRHHEmpleadoNuevo() {
  const navigate = useNavigate();
  const [step, setStep]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [entities, setEntities] = useState([]);
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Personal
    first_name: "", last_name: "", curp: "", rfc: "", nss: "",
    date_of_birth: "", gender: "", marital_status: "",
    phone: "", personal_email: "",
    // Laboral
    branch_id: "", legal_entity_id: "",
    position: "Empleado General", position_code: "EG01",
    department: "operaciones",
    hire_date: new Date().toISOString().slice(0, 10),
    employment_type: "indeterminado",
    schedule_type: "tiempo_completo",
    weekly_hours: 48,
    // Compensación
    salary_type: "quincenal",
    salary_amount: "",
    payment_method: "transferencia",
    tax_regime_employee: "605",
    bank_clabe: "", bank_name: "",
    reports_to: "",
  });

  useEffect(() => {
    api.get("/ops/branches").then(r => setBranches(r.data || [])).catch(() => {});
    api.get("/ops/legal-entities").then(r => setEntities(r.data || [])).catch(() => {});
    api.get("/ops/employees?limit=200").then(r => setManagers(r.data.employees || [])).catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const validate = (stepId) => {
    const errs = {};
    if (stepId === "personal") {
      if (!form.first_name.trim()) errs.first_name = "Requerido";
      if (!form.last_name.trim())  errs.last_name = "Requerido";
    }
    if (stepId === "laboral") {
      if (!form.branch_id)       errs.branch_id = "Requerido";
      if (!form.legal_entity_id) errs.legal_entity_id = "Requerido";
      if (!form.hire_date)       errs.hire_date = "Requerido";
    }
    if (stepId === "compensacion") {
      if (!form.salary_amount || Number(form.salary_amount) <= 0) {
        errs.salary_amount = "Ingresa un salario mayor a $0";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validate(STEPS[step].id)) return;
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    if (!validate(STEPS[step].id)) return;
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        curp: form.curp.trim() || null,
        rfc: form.rfc.trim() || null,
        nss: form.nss.trim() || null,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        marital_status: form.marital_status || null,
        phone: form.phone.trim() || null,
        personal_email: form.personal_email.trim() || null,
        branch_id: form.branch_id,
        legal_entity_id: form.legal_entity_id,
        position: form.position,
        position_code: form.position_code,
        department: form.department || null,
        hire_date: form.hire_date,
        employment_type: form.employment_type,
        schedule_type: form.schedule_type,
        weekly_hours: Number(form.weekly_hours),
        salary_type: form.salary_type,
        salary_amount: Number(form.salary_amount),
        payment_method: form.payment_method,
        tax_regime_employee: form.tax_regime_employee,
        bank_account: {
          bank: form.bank_name || "",
          clabe: form.bank_clabe || "",
          account_number: "",
        },
        reports_to: form.reports_to || null,
      };
      const r = await api.post("/ops/employees", payload);
      toast.success(`Colaborador ${r.data.employee_number} creado`);
      navigate(`/ops/rrhh/empleados/${r.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al crear colaborador");
    } finally {
      setLoading(false);
    }
  };

  const stepId = STEPS[step].id;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          onClick={() => navigate("/ops/rrhh/empleados")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#FFDF00]" />
          Nuevo colaborador
        </h1>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${
                i < step ? "bg-emerald-500 text-white" :
                i === step ? "bg-slate-900 text-white" :
                "bg-slate-200 text-slate-500"
              }`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <span className={`text-sm hidden sm:inline ${i === step ? "font-medium text-slate-900" : "text-slate-400"}`}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-slate-200 mx-1" />}
            </div>
          );
        })}
      </div>

      {/* Form */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">

        {stepId === "personal" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nombre" required error={errors.first_name}>
                <Input value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="input-first-name" />
              </Field>
              <Field label="Apellidos" required error={errors.last_name}>
                <Input value={form.last_name} onChange={e => set("last_name", e.target.value)} data-testid="input-last-name" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="CURP">
                <Input value={form.curp} onChange={e => set("curp", e.target.value.toUpperCase())} maxLength={18} placeholder="XXXX000000XXXXXX00" data-testid="input-curp" />
              </Field>
              <Field label="RFC">
                <Input value={form.rfc} onChange={e => set("rfc", e.target.value.toUpperCase())} maxLength={13} placeholder="XXXX000000XXX" data-testid="input-rfc" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="NSS">
                <Input value={form.nss} onChange={e => set("nss", e.target.value)} maxLength={11} placeholder="12345678901" data-testid="input-nss" />
              </Field>
              <Field label="Fecha de nacimiento">
                <Input type="date" value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} data-testid="input-dob" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Género">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.gender} onChange={e => set("gender", e.target.value)} data-testid="select-gender">
                  <option value="">Sin especificar</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                  <option value="NB">No binario</option>
                </select>
              </Field>
              <Field label="Estado civil">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.marital_status} onChange={e => set("marital_status", e.target.value)} data-testid="select-marital">
                  <option value="">Sin especificar</option>
                  <option value="soltero">Soltero/a</option>
                  <option value="casado">Casado/a</option>
                  <option value="union_libre">Unión libre</option>
                  <option value="divorciado">Divorciado/a</option>
                  <option value="viudo">Viudo/a</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Teléfono">
                <Input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+523221234567" data-testid="input-phone" />
              </Field>
              <Field label="Email personal">
                <Input type="email" value={form.personal_email} onChange={e => set("personal_email", e.target.value)} placeholder="nombre@email.com" data-testid="input-email" />
              </Field>
            </div>
          </>
        )}

        {stepId === "laboral" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sucursal" required error={errors.branch_id}>
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.branch_id} onChange={e => set("branch_id", e.target.value)} data-testid="select-branch">
                  <option value="">Seleccionar...</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Entidad legal" required error={errors.legal_entity_id}>
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.legal_entity_id} onChange={e => set("legal_entity_id", e.target.value)} data-testid="select-entity">
                  <option value="">Seleccionar...</option>
                  {entities.map(e => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Puesto">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.position_code} onChange={e => {
                  const pos = POSITIONS.find(p => p.code === e.target.value);
                  set("position_code", e.target.value);
                  if (pos) set("position", pos.label);
                }} data-testid="select-position">
                  {POSITIONS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Departamento">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.department} onChange={e => set("department", e.target.value)} data-testid="select-department">
                  <option value="operaciones">Operaciones</option>
                  <option value="administracion">Administración</option>
                  <option value="produccion">Producción</option>
                  <option value="mantenimiento">Mantenimiento</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Fecha de ingreso" required error={errors.hire_date}>
                <Input type="date" value={form.hire_date} onChange={e => set("hire_date", e.target.value)} data-testid="input-hire-date" />
              </Field>
              <Field label="Tipo de empleo">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.employment_type} onChange={e => set("employment_type", e.target.value)} data-testid="select-employment">
                  <option value="indeterminado">Tiempo indeterminado</option>
                  <option value="determinado">Tiempo determinado</option>
                  <option value="obra_determinada">Por obra determinada</option>
                  <option value="capacitacion_inicial">Capacitación inicial</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tipo de jornada">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.schedule_type} onChange={e => set("schedule_type", e.target.value)} data-testid="select-schedule">
                  <option value="tiempo_completo">Tiempo completo</option>
                  <option value="medio_tiempo">Medio tiempo</option>
                  <option value="por_horas">Por horas</option>
                </select>
              </Field>
              <Field label="Horas semanales">
                <Input type="number" min={1} max={60} value={form.weekly_hours} onChange={e => set("weekly_hours", e.target.value)} data-testid="input-hours" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Reporta a (jefe directo)">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.reports_to} onChange={e => set("reports_to", e.target.value)} data-testid="select-reports-to">
                  <option value="">Sin jefe directo</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.position})</option>)}
                </select>
              </Field>
            </div>
          </>
        )}

        {stepId === "compensacion" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Periodicidad de pago">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.salary_type} onChange={e => set("salary_type", e.target.value)} data-testid="select-salary-type">
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                  <option value="semanal">Semanal</option>
                  <option value="diario">Diario</option>
                </select>
              </Field>
              <Field label="Monto de sueldo (MXN)" required error={errors.salary_amount}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    className="pl-7"
                    value={form.salary_amount}
                    onChange={e => set("salary_amount", e.target.value)}
                    data-testid="input-salary"
                  />
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Método de pago">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.payment_method} onChange={e => set("payment_method", e.target.value)} data-testid="select-payment">
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="cheque">Cheque</option>
                </select>
              </Field>
              <Field label="Régimen fiscal del trabajador">
                <select className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" value={form.tax_regime_employee} onChange={e => set("tax_regime_employee", e.target.value)} data-testid="select-regime">
                  <option value="605">605 - Sueldos y salarios</option>
                  <option value="606">606 - Arrendamiento</option>
                  <option value="612">612 - Personas Físicas con actividades empresariales</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Banco">
                <Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} placeholder="BBVA, Banorte..." data-testid="input-bank" />
              </Field>
              <Field label="CLABE interbancaria">
                <Input value={form.bank_clabe} onChange={e => set("bank_clabe", e.target.value)} maxLength={18} placeholder="012XXXXXXXXXXXXX" data-testid="input-clabe" />
              </Field>
            </div>

            {form.salary_amount > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="text-slate-500">SDI estimado:</p>
                <p className="font-semibold text-slate-800">
                  ${(
                    form.salary_type === "quincenal" ? Number(form.salary_amount) * 24 / 365 :
                    form.salary_type === "mensual"   ? Number(form.salary_amount) * 12 / 365 :
                    form.salary_type === "semanal"   ? Number(form.salary_amount) * 52 / 365 :
                    Number(form.salary_amount)
                  ).toFixed(4)} MXN/día
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Nav */}
      <div className="flex justify-between">
        {step > 0 ? (
          <Button variant="outline" onClick={() => setStep(s => s - 1)}>
            Anterior
          </Button>
        ) : <div />}

        {step < STEPS.length - 1 ? (
          <Button
            data-testid="next-step-btn"
            onClick={handleNext}
            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
          >
            Siguiente <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            data-testid="submit-employee-btn"
            onClick={handleSubmit}
            disabled={loading}
            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {loading ? "Guardando..." : "Crear colaborador"}
          </Button>
        )}
      </div>
    </div>
  );
}
